import { convertGeometryToOpenLayers } from "@omnia/shared/geom"
import { convertPolygonCoordsToOpenLayers, isPolygonFlatCoordinatesArray } from "@omnia/shared/geom/flatPolygonCoords"
import { arrayMax, arrayMin } from "../../utils/array"
import { isObject } from "../../utils/utils"
import { IMapMeasuringService } from "../measuring/types"
import { JstsGeom } from "./geom"
import { IMapTopologyGeometry, IMapTopologyService } from "./types"
import { ol_format, ol_geom } from "@omnia/exports/openlayers";

/** @internal */
export class JstsTopologyService implements IMapTopologyService {

    private readonly _olParser = createOpenLayersParser()

    private readonly _wktReader = new jsts.io.WKTReader()
    private readonly _wktWriter = new jsts.io.WKTWriter()

    private readonly _olWktFormat = new ol_format.WKT()

    static $inject = ["OmniaMapMeasuringService"]

    constructor(public readonly measure: IMapMeasuringService) {
    }

    getArea(geometry: any): number {

        const geom = this.getGeometry(geometry).getOpenLayersGeometry()

        return this.measure.getArea(geom)

    }


    getGeometry(geometry: any, makeValid = false): IMapTopologyGeometry {

        return this.formatJstsResult(this.readJstsGeometry(geometry, makeValid))

    }

    makeValid(geometry: any) {
        return this.getGeometry(geometry, true)
    }

    getLargestPolygon(geometry: any) {

        const g = getLargestPolygon(this.readJstsGeometry(geometry))

        return this.formatJstsResult(g)

    }

    buffer(geometry: any, distance: number): IMapTopologyGeometry {

        var g = this.readJstsGeometry(geometry);

        g = g.buffer(distance, 2, 2);

        return this.formatJstsResult(g);

    }

    buffer2(geometry: any, distance: number): IMapTopologyGeometry {

        var g = this.readJstsGeometry(geometry);

        g = (<any>g).buffer(distance, 2, 3);

        return this.formatJstsResult(g);

    }

    difference(geometry: any, others: any[]): IMapTopologyGeometry {

        const info = (g: jsts.geom.Geometry) => {

            const wkt = (g: jsts.geom.Geometry) => this.formatJstsResult(g).getWKT()

            return {
                type: g.getGeometryType(),
                area: g.getArea(),
                dim: g.getDimension(),
                wkt: wkt(g)
            }
        }

        const checkCollection = (g: jsts.geom.Geometry) => (g instanceof jsts.geom.GeometryCollection)
            ? getLargestPolygon(g)
            : g

        let outer: jsts.geom.Geometry = this.readJstsGeometry(geometry, true)

        const inner = others.map(o => this.readJstsGeometry(o, true))

        for (const i of inner) {

            outer = checkCollection(outer)
            const ip = checkCollection(i)

            const diff = outer.difference(ip)

            outer = diff

        }

        const result = outer

        return this.formatJstsResult(result)

    }

    intersection2(geometry1: any, geometry2: any, makeValid = false) {

        // Usersnap 1331
        // The call to intersection() was throwing an exception
        // Cannot really do anything about it for now, the problem seems to lie with JSTS,
        // so we are just ignoring such errors from now on
        try {

            const g1 = this.readJstsGeometry(geometry1, makeValid)
            const g2 = this.readJstsGeometry(geometry2, makeValid)

            if (g1 && g2) {

                const i = g1.intersection(g2)

                return {
                    intersection: this.formatJstsResult(i),
                    error: false
                }

            }

        } catch (error) {

            console.error(error)

            return {
                intersection: null,
                error: true
            }

        }

    }

    intersection(geometry1: any, geometry2: any, makeValid = false): IMapTopologyGeometry {

        const { intersection } = this.intersection2(geometry1, geometry2, makeValid)

        return intersection

    }

    union(geometries: any[]): IMapTopologyGeometry {

        const sources = geometries.map(g => this.readJstsGeometry(g)).filter(g => g && g.isValid())
        if (geometries.length != sources.length) return null

        const union = sources.reduce((g1, g2) => g1 && g1.isValid() && g2 && g2.isValid() ? g1.union(g2) : null)

        return union
            ? this.formatJstsResult(union)
            : null


    }

    contains(outer: any, inner: any): boolean {

        var jstsOuter = this.readJstsGeometry(outer);
        var jstsInner = this.readJstsGeometry(inner);

        return jstsOuter.contains(jstsInner);
    }

    intersects(g1: any, g2: any): boolean {

        var jsts1 = this.readJstsGeometry(g1);
        var jsts2 = this.readJstsGeometry(g2);

        return jsts1.intersects(jsts2);
    }

    overlaps(g1: any, g2: any): boolean {

        var jsts1 = this.readJstsGeometry(g1);
        var jsts2 = this.readJstsGeometry(g2);

        return jsts1.overlaps(jsts2);
    }

    crosses(g1: any, g2: any): boolean {

        var jsts1 = this.readJstsGeometry(g1);
        var jsts2 = this.readJstsGeometry(g2);

        return jsts1.crosses(jsts2);
    }

    touches(g1: any, g2: any): boolean {

        var jsts1 = this.readJstsGeometry(g1);
        var jsts2 = this.readJstsGeometry(g2);

        return jsts1.touches(jsts2);
    }

    disjoint(g1: any, g2: any): boolean {

        try {

            const jsts1 = this.readJstsGeometry(g1, true);
            const jsts2 = this.readJstsGeometry(g2, true);

            return !jsts1.intersects(jsts2);

        }
        catch (error) {
            console.error(error)
            return true
        }


    }

    polygonize(geometries: any[]): IMapTopologyGeometry[] {

        var jstsGeometries = geometries.
            map(g => this.readJstsGeometry(g)).
            map(g => (<any>g).getExteriorRing ? (<any>g).getExteriorRing() : g);

        var union = jstsGeometries.reduce((p1, p2) => p1.union(p2));

        var polygonizer = new (<any>jsts).operation.polygonize.Polygonizer();
        polygonizer.add(union);

        var polygons = polygonizer.getPolygons();

        var results: jsts.geom.Geometry[] = [];

        for (var i = polygons.iterator(); i.hasNext();) {

            var geom = <jsts.geom.Geometry>i.next();

            if (geom.getGeometryType() == "Polygon") {
                results.push(geom);
            }

        }

        return results.map(g => this.formatJstsResult(g));

    }


    cleanupInteriorRings(geometry: IMapTopologyGeometry, minimumArea: number): IMapTopologyGeometry {

        const source = geometry.getOpenLayersGeometry();

        if (!(source instanceof ol_geom.Polygon)) {
            return geometry
        }

        var target = new ol_geom.Polygon([source.getLinearRing(0).getCoordinates()]);

        for (var i = 1; i < source.getLinearRingCount(); i++) {

            let ring = source.getLinearRing(i);

            if (ring.getArea) {
                let area = Math.abs(ring.getArea());
                if (area >= minimumArea) {
                    target.appendLinearRing(ring.clone());
                }
            }

        }

        return this.formatOpenLayersResult(target);

    }

    geometryCollectionToArray(geometry: any): IMapTopologyGeometry[] {

        const geom = this.readJstsGeometry(geometry)
        if (!geom) return []

        const num = geom.getNumGeometries();

        if (num > 1) {

            const array: jsts.geom.Geometry[] = [];

            for (var i = 0; i < num; i++) {
                array.push(geom.getGeometryN(i));
            }

            return array.map(g => this.formatJstsResult(g));

        }

        return [this.formatJstsResult(geom)];

    }

    getEnvelope(geometries: any[]): { minx: number, miny: number, maxx: number, maxy: number } {

        const ee = geometries
            .map(g => this.readJstsGeometry(g).getEnvelopeInternal())

        const minx = arrayMin(ee, e => e.getMinX())
        const miny = arrayMin(ee, e => e.getMinY())
        const maxx = arrayMax(ee, e => e.getMaxX())
        const maxy = arrayMax(ee, e => e.getMaxY())

        return { minx, miny, maxx, maxy }

    }

    getConvexHull(geometry: any): IMapTopologyGeometry {

        const geom = this.readJstsGeometry(geometry)

        return this.formatJstsResult(geom.convexHull())

    }

    getInteriorPoint(geometry: any): [number, number] {
        return this.getPointProperty(geometry, g => g.getInteriorPoint())
    }

    getCentroid(geometry: any): [number, number] {
        return this.getPointProperty(geometry, g => g.getCentroid())
    }

    private getPointProperty(geometry: any, selector: (jstsGeom: jsts.geom.Geometry) => jsts.geom.Point): [number, number] {

        const geom = this.readJstsGeometry(geometry)

        if (geom) {

            const point = selector(geom)

            if (point) {
                return [point.getX(), point.getY()]
            }

        }

    }


    private formatOpenLayersResult(geometry: any): IMapTopologyGeometry {

        if (geometry) {

            var jsts = this.readJstsGeometry(geometry);

            var wkt = this._olWktFormat.writeGeometry(geometry);

            return new JstsGeom(jsts, geometry, wkt);

        }

        return new JstsGeom(null, null, null);

    }

    private formatJstsResult(geometry: jsts.geom.Geometry): IMapTopologyGeometry {

        if (geometry && (!geometry.isEmpty())) {

            var wkt = this._wktWriter.write(geometry);
            var ol = this._olWktFormat.readGeometry(wkt);

            return new JstsGeom(geometry, ol, wkt);

        }

        return new JstsGeom(null, null, null);

    }

    private readJstsGeometry(geometry: any, makeValid = false): jsts.geom.Geometry {

        const read = () => {

            if (geometry instanceof JstsGeom) {
                return geometry.getJstsGeometry()
            }

            if (isPolygonFlatCoordinatesArray(geometry)) {
                geometry = convertPolygonCoordsToOpenLayers(geometry)
            }

            if (isObject(geometry)) {

                if (geometry.getOpenLayersGeometry) {
                    geometry = geometry.getOpenLayersGeometry()
                }
            }

            geometry = convertGeometryToOpenLayers(geometry)

            return this._olParser.read(geometry)

        }

        let g = read()

        if (g && makeValid && (!g.isValid())) {
            g = g.buffer(0, 2, 2)
        }

        return g

    }

}

function getLargestPolygon(geom: jsts.geom.Geometry) {

    const num = geom.getNumGeometries()

    if (num > 1) {

        let largest = { geom: undefined as jsts.geom.Geometry, area: -1 }

        for (let i = 0; i < num; i++) {

            const p = geom.getGeometryN(i)

            if (p && p.getArea) {
                const a = p.getArea()
                if (a > largest.area) {
                    largest = {
                        geom: p,
                        area: a
                    }
                }
            }

        }

        geom = largest.geom

    }

    return geom

}
function createOpenLayersParser() {

    const p = new jsts.io.OL3Parser()

    p.inject(
        ol_geom.Point,
        ol_geom.LineString,
        ol_geom.LinearRing,
        ol_geom.Polygon,
        ol_geom.MultiPoint,
        ol_geom.MultiLineString,
        ol_geom.MultiPolygon,
        ol_geom.GeometryCollection
    )

    return p

}

